import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-hoodie-spreadsheet";
const articleImage = "https://readdy.ai/api/search-image?query=Three%20folded%20hoodies%20in%20neutral%20beige%20gray%20and%20cream%20colors%20arranged%20on%20warm%20textured%20linen%20surface%2C%20soft%20natural%20window%20light%2C%20minimalist%20streetwear%20fashion%20photography%2C%20warm%20earthy%20tones%2C%20clean%20scandinavian%20aesthetic%2C%20casual%20wear%20editorial%20catalog%2C%20fabric%20texture%20detail%20visible%2C%20calm%20cozy%20atmosphere&width=1200&height=800&seq=kakobuy-article-hoodie&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Kakobuy Hoodie Spreadsheet — Streetwear, Bluzy i Moda w Najlepszych Cenach",
    description: "Kompletny przewodnik po kategorii Hoodie/Sweaters w Kakobuy Spreadsheet. Odkryj najlepsze bluzy, swetry i odzież streetwearową z linkami do sprawdzonych ofert.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Kakobuy Hoodie Spreadsheet", path: articlePath },
  ]),
];

export default function KakobuyHoodieSpreadsheetArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Kakobuy Hoodie Spreadsheet</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-accent-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shirt-line text-accent-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-accent-700 whitespace-nowrap">Streetwear & Moda</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Hoodie Spreadsheet — Streetwear, Bluzy i Moda w Najlepszych Cenach
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Kompletny przewodnik po kategorii Hoodie/Sweaters w Kakobuy Spreadsheet. Odkryj najlepsze bluzy, swetry i odzież streetwearową z linkami do sprawdzonych ofert.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 9 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Three%20folded%20hoodies%20in%20neutral%20beige%20gray%20and%20cream%20colors%20arranged%20on%20warm%20textured%20linen%20surface%2C%20soft%20natural%20window%20light%2C%20minimalist%20streetwear%20fashion%20photography%2C%20warm%20earthy%20tones%2C%20clean%20scandinavian%20aesthetic%2C%20casual%20wear%20editorial%20catalog%2C%20fabric%20texture%20detail%20visible%2C%20calm%20cozy%20atmosphere&width=1200&height=800&seq=kakobuy-article-hoodie&orientation=landscape"
                alt="Kakobuy Hoodie Spreadsheet — Streetwear i moda"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Kategoria Hoodie/Sweaters — druga najpopularniejsza w arkuszu</h2>
                <p className="mb-3">Kategoria Hoodie/Sweaters to druga najczęściej przeglądana kategoria w Kakobuy Spreadsheet, zaraz po Shoes. Bluzy, swetry i odzież wierzchnia cieszą się ogromnym zainteresowaniem, szczególnie w okresie jesienno-zimowym.</p>
                <p className="mb-3">W arkuszu znajdziesz setki produktów — od klasycznych bluz z kapturem, przez modne swetry, aż po lekkie bluzy typu zip-up. Każdy produkt opatrzony jest linkiem bezpośrednio do oferty, a często także dodatkowymi informacjami o cenie i dostępnych rozmiarach.</p>
                <p>Dzięki zdjęciom QC możesz zobaczyć rzeczywisty wygląd bluzy przed zakupem — sprawdzić jakość materiału, szwy i detale, które często nie są widoczne na zdjęciach produktowych.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Style i rodzaje bluz dostępne w arkuszu</h2>
                <div className="space-y-3">
                  {[
                    { t: "Oversized Hoodie", d: "Luźne bluzy z kapturem w stylu oversized — hit streetwearu. Miękki materiał, duży kaptur i wygodna kieszeń typu kangurka.", styl: "Streetwear / Casual" },
                    { t: "Vintage Wash", d: "Bluzy z efektem sprania i postarzenia. Wyglądają jak noszone od lat, ale są nowe. Świetnie komponują się z jeansami.", styl: "Vintage / Retro" },
                    { t: "Minimalist Zip-Up", d: "Bluzy zapinane na zamek w minimalistycznym stylu. Czyste linie, brak nadruków, stonowane kolory.", styl: "Minimalist / Clean" },
                    { t: "Graphic Hoodie", d: "Bluzy z nadrukami — od prostych logo po artystyczne grafiki. Wyrazisty element każdej stylizacji.", styl: "Streetwear / Art" },
                    { t: "Heavyweight Knit Sweater", d: "Swetry z grubej dzianiny — ciepłe, trwałe i stylowe. Idealne na chłodne dni.", styl: "Classic / Casual" },
                  ].map((item) => (
                    <div key={item.t} className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-foreground-900">{item.t}</h3>
                        <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">{item.styl}</span>
                      </div>
                      <p className="text-xs text-foreground-600">{item.d}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Porównanie jakości i cen — czego się spodziewać?</h2>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/70">
                        <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground-700 whitespace-nowrap">Przedział cenowy</th>
                        <th className="text-left py-2 text-xs font-semibold text-foreground-700 whitespace-nowrap">Czego się spodziewać</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["60–120 zł", "Podstawowe bluzy — przyzwoita jakość, dobry wybór na co dzień"],
                        ["120–200 zł", "Lepsze materiały i wykończenie — grubszy materiał, staranniejsze szwy"],
                        ["200–350 zł", "Wysoka jakość — ciężki materiał, dopracowane detale, często repliki znanych marek"],
                        ["350+ zł", "Premium — najlepsze materiały, perfekcyjne wykończenie, limitowane modele"],
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-background-200/70 last:border-0">
                          <td className="py-2.5 pr-4 text-foreground-800 font-medium whitespace-nowrap">{row[0]}</td>
                          <td className="py-2.5 text-foreground-600 text-xs">{row[1]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Znajdź swoją idealną bluzę</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Setki stylowych bluz i swetrów czeka na Ciebie w arkuszu Kakobuy Spreadsheet.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}